import React, { useState } from 'react';
import { 
  List, 
  ListItem, 
  ListItemText, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  ListItemIcon,
  Box,
  Chip
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { MoreVert, Delete, PlayArrow, OndemandVideo, Label } from '@mui/icons-material';
import { Project, ProjectWithSlideCount, Tag } from '../../types';
import { useLang } from '../../contexts/langContext';
import ManageTagsDialog from '../common/ManageTagsDialog';

interface ProjectListProps {
  projects: ProjectWithSlideCount[];
  onDeleteProject: (id: string) => void;
  onProjectUpdated?: (id: string, updates: Partial<ProjectWithSlideCount>) => void;
}

const ProjectList: React.FC<ProjectListProps> = ({ projects, onDeleteProject, onProjectUpdated }) => {
  const { lang } = useLang();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProject, setSelectedProject] = useState<ProjectWithSlideCount | null>(null);
  const [manageTagsDialogOpen, setManageTagsDialogOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, project: ProjectWithSlideCount) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedProject(project);
    console.log('selectedProject', project);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProject(null);
  };

  const handleDeleteClick = () => {
    if (selectedProject) {
      onDeleteProject(selectedProject._id);
    }
    handleMenuClose();
  };

  const handleManageTagsClick = () => {
    setManageTagsDialogOpen(true);
    setAnchorEl(null);
  };

  const handleTagsSaved = (newTagIds: string[]) => {
    (async () => {
      if (selectedProject && onProjectUpdated) {
        try {
          const { getTags } = await import('../../services/tags');
          const allTags = await getTags();
          const newTags = allTags.filter((tag: Tag) => newTagIds.includes(tag._id));
          onProjectUpdated(selectedProject._id, { tagIds: newTagIds, tags: newTags });
        } catch (err) {
          console.error('Failed to refresh tags locally', err);
          onProjectUpdated(selectedProject._id, { tagIds: newTagIds });
        }
      }
    })();
  };

  return (
    <>
      <List>
        {projects.map((project, index) => (
          <React.Fragment key={project._id}>
            <ListItem
              button
              component={RouterLink}
              to={`/projects/${project._id}`}
              secondaryAction={
                <IconButton
                  edge="end"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={(e) => handleMenuOpen(e, project)}
                >
                  <MoreVert />
                </IconButton>
              }
              sx={{
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                cursor: 'pointer',
              }}
            >
              <ListItemIcon>
                <OndemandVideo />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography component="span" variant="body1">
                      {project.title}
                    </Typography>
                    {project.tags && project.tags.map((tag) => (
                      <Chip
                        key={tag._id}
                        label={tag.name}
                        size="small"
                        sx={{ height: 20 }}
                      />
                    ))}
                  </Box>
                }
                secondary={
                  <>
                    <Typography component="span" variant="body2" color="text.primary">
                      {project.description}
                    </Typography>
                    {` — ${project.slideCount} ${lang === 'en' ? 'slides' : '页'} — ${new Date(project.createdAt).toLocaleDateString()}`}
                  </>
                }
              />
            </ListItem>
          </React.Fragment>
        ))}
      </List>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleManageTagsClick}>
          <Label fontSize="small" style={{ marginRight: 8 }} color="action" />
          {lang === 'en' ? 'Manage Tags' : '标签'}
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <Delete fontSize="small" style={{ marginRight: 8 }} color="action" />
          {lang === 'en' ? 'Delete' : '删除'}
        </MenuItem>
      </Menu>

      {manageTagsDialogOpen && selectedProject && (
        <ManageTagsDialog
          open
          onClose={() => {
            setManageTagsDialogOpen(false);
            setSelectedProject(null);
          }}
          entityType="project"
          entity={selectedProject}
          onSaved={handleTagsSaved}
        />
      )}
    </>
  );
};

export default ProjectList;
